import React, { useState, useMemo } from 'react';
import { View, Text, TextInput, TouchableOpacity, Platform, useWindowDimensions } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { useTheme } from '../../../../theme/useTheme';
import { KfaApiItem, ObservationsApiItem, RackingInspectionApiItem, NhoApiItem, SiteEvaluationApiItem } from '../../../../store/slices/adminSlice';
import LHDataTable from './KfaDataTable';
import LHFilter, { FilterItem } from '../../../../components/LHFilter';
import FontFamily from '../../../../constants/fonts';
import KfaAddOverlay, { KfaEditItem } from './KfaAddOverlay';
import ObservationAddOverlay, { ObservationEditItem } from './ObservationAddOverlay';
import RackingInspectionAddOverlay, { RackingInspectionEditItem } from './RackingInspectionAddOverlay';
import NhoAddOverlay, { NhoEditItem } from './NhoAddOverlay';
import SiteEvaluationOverlay, { SiteEvaluationEditItem } from './SiteEvaluationOverlay';
import { RACKING_SECTION_OPTIONS } from './kfaDropdownOptions';
import { styles } from './KfaTable.styles';

type KfaTab = 'shift-handover' | 'leadership-walk' | 'sparc-walk' | 'observations' | 'nho' | 'racking-inspection' | 'site-evaluation';

interface KfaTableProps {
  activeTab: KfaTab;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  filterOptions: FilterItem[];
  onFilterChange: (key: string, value: any) => void;
  onResetFilters: () => void;
  selectedModule?: string;
  searchText: string;
  onSearchChange: (text: string) => void;
  onSaveSuccess?: () => void;
}

const MODULE_COLORS: { [key: string]: { bg: string; text: string } } = {
  Observations: { bg: '#E1E3FD', text: '#31217D' },
  'Shift Handover': { bg: '#DAEEFA', text: '#01346F' },
  'Leadership Walk': { bg: '#CAF3C6', text: '#0C420B' },
  'SPARC Walk': { bg: '#FCECDE', text: '#4D2E0C' },
  'NHO': { bg: '#FFF9C4', text: '#F57F17' },
  'Racking Inspection': { bg: '#F3E8FF', text: '#5B21B6' },
  'Site Evaluation': { bg: '#FFE5DE', text: '#AB2B09' },
};

const normalizeMultiValue = (value: unknown): string[] => {
  if (value == null) return [];

  if (Array.isArray(value)) {
    return value.flatMap((item) => normalizeMultiValue(item));
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];

    // Handle JSON-encoded arrays: "[\"Pulse Check\",\"Full\"]"
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.flatMap((item) => normalizeMultiValue(item));
        }
      } catch {
        // Fall through to comma-split handling
      }
    }

    return trimmed
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const candidateKeys = ['displayText', 'label', 'name', 'value', 'roleName', 'observationTypeName'];

    for (const key of candidateKeys) {
      if (obj[key] != null) {
        return normalizeMultiValue(obj[key]);
      }
    }
  }

  return [String(value).trim()].filter(Boolean);
};

// Maps the integrated tab-1 API item to a flat row including placeholder module-specific fields.
const mapMainTabItem = (item: KfaApiItem, zoneMap: Record<number, string>) => {
  const zoneNames = Array.isArray(item.Zone_Ids)
    ? item.Zone_Ids.map((id) => zoneMap[id] ?? `Zone ${id}`).join(', ')
    : '';
  return {
    id: String(item.ID),
    keyFocusArea: item.Negative_Behavior ?? '',
    question: item.Question ?? '',
    modules: Array.isArray(item.Walk_s) ? item.Walk_s.join(', ') : '',
    category: item.SPARC_Category ?? '',
    createdBy: item.Created_By ?? '',
    createdDate: item.Created ? item.Created.split('T')[0] : '',
    createdAt: item.Created ?? undefined,
    // Zone IDs preserved for edit prefill
    zoneIds: Array.isArray(item.Zone_Ids) ? item.Zone_Ids : [],
    // Shared attribute fields for edit prefill
    locationType: item.Location_Type ?? '',
    organizationType: item.Organization_Type ?? '',
    is3pl: item.Is_3pl,
    // Shift Handover specific — user to map from API field
    shiftHandoverSection: '',
    // Leadership Walk specific
    leadershipWalkZone: zoneNames,
    leadershipWalkFrequency: item.Frequency ?? '',
    sparcWalkZone: zoneNames,// SPARC Walk specific
    sparcWalkSection: item.section?? '',
    // Status: API may return Status (boolean) for enabled/disabled
    status: (item as any).Status ?? (item as any).status ?? undefined,
  };
};

// Maps Observations API item to table row
const mapObservationsItem = (item: ObservationsApiItem) => {
  const obsTypeValues = [
    ...normalizeMultiValue((item as any).observationTypeNames),
    ...normalizeMultiValue((item as any).observationTypeName),
    ...normalizeMultiValue((item as any).observationType),
  ];
  const obsRoleValues = [
    ...normalizeMultiValue((item as any).roleNames),
    ...normalizeMultiValue((item as any).roleName),
    ...normalizeMultiValue((item as any).roles),
    ...normalizeMultiValue((item as any).role),
  ];

  const uniqueObsTypeValues = Array.from(new Set(obsTypeValues));
  const uniqueObsRoleValues = Array.from(new Set(obsRoleValues));

  const obsTypeDisplay = uniqueObsTypeValues.join(', ');
  const obsRolesDisplay = uniqueObsRoleValues.join(', ');
  
  return {
    id: String(item.id ?? item.ID ?? ''),
    keyFocusArea: item.observation ?? item.keyFocusArea ?? item.negativeObservation ?? item.sectionValue ?? '',
    question: item.question ?? undefined,
    category: item.sparcCategory ?? item.currentCategory ?? '',
    obsType: obsTypeDisplay,
    obsRoles: obsRolesDisplay,
    obsSection: item.section ?? item.sectionValue ?? item.buildingStatus ?? '',
    createdBy: item.createdBy ?? '',
    createdDate: item.createdAt ? item.createdAt.split('T')[0] : '',
    createdAt: item.createdAt ?? undefined,
    // Shared attribute fields
    locationType: item.locationType ?? item.LocationType ?? item.locationTypeName ?? undefined,
    organizationType: item.organizationType ?? item.OrganizationType ?? item.orgType ?? (item as any).orgTypes ?? undefined,
    // Normalize threePl/3PL values: prefer explicit threePlStatus (YES/NO/ANY), fall back to boolean flags
    threePlStatus: item.threePlStatus ?? undefined,
    is3pl: item.threePlStatus != null
      ? (String(item.threePlStatus).toUpperCase() === 'YES' ? true : String(item.threePlStatus).toUpperCase() === 'NO' ? false : undefined)
      : item.is3pl ?? (item as any).Is3PL ?? (item as any).is_3pl ?? undefined,
    status: item.status,
  };
};

// Maps Racking Inspection API item to table row
const mapRackingInspectionItem = (item: RackingInspectionApiItem) => ({
  id: String(item.rowKey ?? ''),
  keyFocusArea: item.qtext ?? '',
  question: item.shortForm ?? undefined,
  modules: item.modules ?? item.module ?? '',
  locationType: Array.isArray(item.locationTypes) && item.locationTypes.length > 0
    ? item.locationTypes.join(', ')
    : (item.locationType ?? undefined),
  organizationType: item.organizationType ?? item.orgType ?? (item as any).orgTypes ?? undefined,
  is3pl: item.threePlStatus != null
    ? (String(item.threePlStatus).toUpperCase() === 'YES' ? true : String(item.threePlStatus).toUpperCase() === 'NO' ? false : undefined)
    : undefined,
  category: item.category ?? undefined,
  status: item.status,
  rackingType: item.rackingType ?? item.type ?? item.typeName ?? (item as any).rackingInspectionType ?? undefined,
  rackingRoles: item.roleName ?? item.role ?? (Array.isArray(item.roles) ? item.roles.join(', ') : undefined),
  rackingSection: item.section ?? undefined,
  createdBy: item.createdBy ?? '',
  createdDate: item.createdAt ? item.createdAt.split('T')[0] : '',
  createdAt: item.createdAt ?? undefined,
});

// Maps NHO API item to table row
const mapNhoItem = (item: NhoApiItem, headers: NhoApiItem[]) => {
  const header = headers.find((h) => h.id === item.parentId);
  const touchTypeValues = Array.isArray(item.touchTypes)
    ? item.touchTypes.filter((value) => value !== 'Preferred_Method')
    : [];
  if (Number(item.preferredMethod ?? 0) === 1) {
    touchTypeValues.push('Preferred_Method');
  }
  const uniqueTouchTypes = Array.from(new Set(touchTypeValues));
  const nhoTouchTypeDisplay = uniqueTouchTypes
    .map((value) => value.replace(/_/g, ' '))
    .join(', ');

  let is3pl: boolean | undefined;
  if (item.threePlStatus != null) {
    is3pl = String(item.threePlStatus).toUpperCase() === 'YES';
  } else if (typeof item.is3pl === 'boolean') {
    is3pl = item.is3pl;
  }
  return {
    id: String(item.id),
    keyFocusArea: item.sectionValue ?? '',
    nhoTouchType: nhoTouchTypeDisplay,
    nhoSection: item.sectionType === 'Sub-Data'
      ? [item.sectionSubHeader, item.sectionHeader].filter(Boolean).join(' → ')
      : item.sectionType === 'Data'
      ? (item.sectionHeader ?? '')
      : (header?.sectionValue ?? (item.section ? `Section ${item.section}` : '')),
    locationType: Array.isArray(item.locationTypes) && item.locationTypes.length > 0
      ? item.locationTypes.join(', ')
      : (item.locationType ?? ''),
    organizationType: item.organizationType ?? '',
    is3pl,
    category: item.category ?? '',
    status: item.status,
    createdAt: item.createdAt ?? undefined,
  };
};

// Maps Site Evaluation API item to table row
const mapSiteEvaluationItem = (item: SiteEvaluationApiItem) => {
  const sectionTypeUpper = (item.sectionType ?? '').toUpperCase();
  // For HEADER rows the section column shows the sectionValue (it IS the header)
  // For DATA rows the section column shows the parent header name
  const siteSection =
    sectionTypeUpper === 'HEADER'
      ? item.sectionValue ?? ''
      : item.sectionHeader ?? item.sectionValue ?? '';
  return {
    id: String(item.id),
    keyFocusArea: item.sectionValue ?? '',
    locationType: item.locationType ?? '',
    organizationType: item.organizationType ?? '',
    is3pl: item.threePlStatus === true ? true : item.threePlStatus === false ? false : undefined,
    category: item.category ?? '',
    status: item.status,
    siteSection,
    createdAt: item.createdAt ?? undefined,
  };
};

const COMMON_START_COLS = [
  { columnKey: 'keyFocusArea', header: 'Key Focus Area', width: 280 },
  { columnKey: 'question', header: 'Question', width: 260 },
];

// const COMMON_END_COLS = [
//   { columnKey: 'createdBy', header: 'Created By', width: 160 },
//   { columnKey: 'createdDate', header: 'Created Date', width: 150 },
// ];

const COMMON_END_COLS: any[] = [
  // { columnKey: 'createdBy', header: 'Created By', width: 160 },
  // { columnKey: 'createdDate', header: 'Created Date', width: 150 },
];

const MODULES_COL = {
  columnKey: 'modules',
  header: 'Modules',
  width: 200,
  renderCell: (item: any) => {
    const knownModules = Object.keys(MODULE_COLORS).sort((first, second) => second.length - first.length);

    const moduleList = Array.isArray(item.modules)
      ? item.modules.map((moduleName: any) => String(moduleName).trim()).filter(Boolean)
      : (() => {
          const modulesValue = String(item.modules ?? '').trim();
          const parsedModules: string[] = [];
          let remaining = modulesValue.replace(/,/g, ' ').replace(/\s+/g, ' ').trim();

          while (remaining.length > 0) {
            const matchedModule = knownModules.find((moduleName) =>
              remaining.toLowerCase().startsWith(moduleName.toLowerCase())
            );

            if (matchedModule) {
              parsedModules.push(matchedModule);
              remaining = remaining.slice(matchedModule.length).trimStart();
              continue;
            }

            const nextSpaceIndex = remaining.indexOf(' ');
            if (nextSpaceIndex === -1) {
              break;
            }
            remaining = remaining.slice(nextSpaceIndex + 1).trimStart();
          }

          return parsedModules.length > 0 ? parsedModules : (modulesValue ? [modulesValue] : []);
        })();

    return (
      <View style={styles.modulesCell}>
        {moduleList.map((mod: string, i: number) => {
          const colors = MODULE_COLORS[mod] || { bg: '#F5F9FF', text: '#005CBC' };
          return (
            <View key={i} style={[styles.moduleBadge, { backgroundColor: colors.bg }]}>
              <Text style={[styles.moduleBadgeText, { color: colors.text }]}>{mod}</Text>
            </View>
          );
        })}
      </View>
    );
  },
};

const CATEGORY_COLORS_LIGHT: { [key: string]: { bg: string; text: string } } = {
  Safety:      { bg: '#FFE5DE', text: '#AB2B09' },
  People:      { bg: '#E1E3FD', text: '#31217D' },
  Accuracy:    { bg: '#CAF3C6', text: '#0C420B' },
  Reliability: { bg: '#DAEEFA', text: '#01346F' },
  Costs:        { bg: '#FEF1B9', text: '#995211' },
};

const CATEGORY_COLORS_DARK: { [key: string]: { bg: string; text: string } } = {
  Safety:      { bg: 'rgba(220,53,53,0.22)',   text: '#FFB09A' },
  People:      { bg: 'rgba(124,92,216,0.22)',  text: '#C4B5FD' },
  Accuracy:    { bg: 'rgba(56,182,56,0.22)',   text: '#8FEBA8' },
  Reliability: { bg: 'rgba(61,139,255,0.22)',  text: '#99CFFF' },
  Costs:        { bg: 'rgba(217,150,0,0.22)',   text: '#FFD166' },
};

// Racking Inspection Section formatter — maps short codes to full display names
const formatRackingSection = (shortCode: string | undefined): string => {
  if (!shortCode) return '—';
  const option = RACKING_SECTION_OPTIONS.find((opt) => opt.id === shortCode);
  return option ? option.displayText : shortCode;
};

const renderOrgTypeCell = (item: any, isDark: boolean = false) => {
  const value = item.organizationType ?? '';
  if (!value || value === '-') {
    return <Text style={{ fontSize: 13, color: isDark ? '#D4D4D4' : '#2D2D2D' }}>-</Text>;
  }
  const orgs = String(value).split(',').map((v: string) => v.trim()).filter(Boolean);
  return (
    <View style={styles.locationTypeCell}>
      {orgs.map((org: string, i: number) => (
        <View
          key={i}
          style={[
            styles.orgTypeBadge,
            isDark && { backgroundColor: 'rgba(61,139,255,0.22)' },
          ]}
        >
          <Text
            style={[
              styles.orgTypeBadgeText,
              isDark && { color: '#99CFFF' },
            ]}
          >
            {org}
          </Text>
        </View>
      ))}
    </View>
  );
};

const renderCategoryCell = (item: any, isDark: boolean = false) => {
  const value = item.category ?? '';
  if (!value || value === '-') {
    return <Text style={{ fontSize: 13, color: isDark ? '#D4D4D4' : '#2D2D2D' }}>-</Text>;
  }
  const palette = isDark ? CATEGORY_COLORS_DARK : CATEGORY_COLORS_LIGHT;
  const categories = String(value).split(',').map((v: string) => v.trim()).filter(Boolean);
  return (
    <View style={styles.locationTypeCell}>
      {categories.map((cat: string, i: number) => {
        const colors = palette[cat] ?? {
          bg: isDark ? 'rgba(255,255,255,0.12)' : '#F0F0F0',
          text: isDark ? '#E5E5E5' : '#333333',
        };
        return (
          <View key={i} style={[styles.locationTypeBadge, { backgroundColor: colors.bg }]}>
            <Text style={[styles.locationTypeBadgeText, { color: colors.text }]}>{cat}</Text>
          </View>
        );
      })}
    </View>
  );
};

const renderLocationTypeCell = (item: any, isDark: boolean = false) => {
  const value = item.locationType ?? '';
  if (!value || value === '-') {
    return <Text style={{ fontSize: 13, color: isDark ? '#D4D4D4' : '#2D2D2D' }}>-</Text>;
  }
  const locations = String(value).split(',').map((v: string) => v.trim()).filter(Boolean);
  return (
    <View style={styles.locationTypeCell}>
      {locations.map((loc: string, i: number) => (
        <View
          key={i}
          style={[
            styles.locationTypeBadge,
            isDark && { backgroundColor: 'rgba(61,139,255,0.22)' },
          ]}
        >
          <Text
            style={[
              styles.locationTypeBadgeText,
              isDark && { color: '#99CFFF' },
            ]}
          >
            {loc}
          </Text>
        </View>
      ))}
    </View>
  );
};

const LOCATION_TYPE_COL = {
  columnKey: 'locationType',
  header: 'Location Type',
  width: 150,
  renderCell: (item: any) => renderLocationTypeCell(item, false),
};

// Shared column fragments (no Modules column for individual tabs)
const SHARED_ATTR_COLS = [
  { columnKey: 'category', header: 'Category', width: 140, renderCell: renderCategoryCell },
  LOCATION_TYPE_COL,
  { columnKey: 'organizationType', header: 'Organization Type', width: 170, renderCell: renderOrgTypeCell },
  {
    columnKey: 'is3pl',
    header: '3PL Status',
    width: 120,
    formatter: (value: boolean | undefined) =>
      value === true ? 'Yes' : value === false ? 'No' : '—',
    getSortValue: (item: any) =>
      item.is3pl === true ? 'Yes' : item.is3pl === false ? 'No' : '',
  },
  {
    columnKey: 'status',
    header: 'Status',
    width: 120,
    formatter: (value: boolean | undefined | null) =>
      value === true ? 'Enabled' : value === false ? 'Disabled' : '',
    getSortValue: (item: any) =>
      item.status === true ? 'Enabled' : item.status === false ? 'Disabled' : '',
  },
];

const TAB_COLUMNS: Record<KfaTab, any[]> = {
  'shift-handover': [
    ...COMMON_START_COLS,
    ...SHARED_ATTR_COLS,
    ...COMMON_END_COLS,
  ],
  'leadership-walk': [
    ...COMMON_START_COLS,
    ...SHARED_ATTR_COLS,
    { columnKey: 'leadershipWalkZone', header: 'Zone', width: 210 },
    { columnKey: 'leadershipWalkFrequency', header: 'Frequency', width: 200 },
    ...COMMON_END_COLS,
  ],
  'sparc-walk': [
    ...COMMON_START_COLS,
    ...SHARED_ATTR_COLS,
    { columnKey: 'sparcWalkZone', header: 'Zone', width: 200 },
    { columnKey: 'sparcWalkSection', header: 'Section', width: 210 },
    ...COMMON_END_COLS,
  ],
  observations: [
    ...COMMON_START_COLS,
    { columnKey: 'category', header: 'Category', width: 140, renderCell: renderCategoryCell },
    LOCATION_TYPE_COL,
    { columnKey: 'organizationType', header: 'Organization Type', width: 170, renderCell: renderOrgTypeCell },
    {
      columnKey: 'is3pl',
      header: '3PL Status',
      width: 120,
      formatter: (value: boolean | undefined) =>
        value === true ? 'Yes' : value === false ? 'No' : '—',
      getSortValue: (item: any) =>
        item.is3pl === true ? 'Yes' : item.is3pl === false ? 'No' : '',
    },
    {
      columnKey: 'status',
      header: 'Status',
      width: 120,
      formatter: (value: boolean | undefined | null) =>
        value === true ? 'Enabled' : value === false ? 'Disabled' : '',
      getSortValue: (item: any) =>
        item.status === true ? 'Enabled' : item.status === false ? 'Disabled' : '',
    },
    
    { columnKey: 'obsType', header: 'Observation Types', width: 230 },
    { columnKey: 'obsRoles', header: 'Observation - Roles', width: 215 },
    { columnKey: 'obsSection', header: 'Observation - Section', width: 220 },
  
    ...COMMON_END_COLS,
  ],
  nho: [
    { columnKey: 'keyFocusArea', header: 'Key Focus Area', width: 280 },
    LOCATION_TYPE_COL,
    { columnKey: 'organizationType', header: 'Org Types', width: 170, renderCell: renderOrgTypeCell },
    {
      columnKey: 'is3pl',
      header: '3PL Status',
      width: 120,
      formatter: (value: boolean | undefined) =>
        value === true ? 'Yes' : value === false ? 'No' : '-',
      getSortValue: (item: any) =>
        item.is3pl === true ? 'Yes' : item.is3pl === false ? 'No' : '',
    },
    { columnKey: 'category', header: 'Category', width: 140, renderCell: renderCategoryCell },
    {
      columnKey: 'status',
      header: 'Status',
      width: 120,
      formatter: (value: boolean | undefined | null) =>
        value === true ? 'Enabled' : value === false ? 'Disabled' : '-',
      getSortValue: (item: any) =>
        item.status === true ? 'Enabled' : item.status === false ? 'Disabled' : '',
    },
    { columnKey: 'nhoTouchType', header: 'NHO - Touch Type', width: 200, formatter: (value: any) => (value != null && value !== '' ? value : '-') },
    { columnKey: 'nhoSection', header: 'NHO - Section', width: 190, formatter: (value: any) => (value != null && value !== '' ? value : '-') },
    ...COMMON_END_COLS,
  ],
  'racking-inspection': [
    { columnKey: 'keyFocusArea', header: 'Key Focus Area', width: 280 },
    { columnKey: 'question', header: 'Short Form', width: 260 },
    LOCATION_TYPE_COL,
    { columnKey: 'organizationType', header: 'Org Types', width: 170, renderCell: renderOrgTypeCell },
    {
      columnKey: 'is3pl',
      header: '3PL Status',
      width: 120,
      formatter: (value: boolean | undefined) =>
        value === true ? 'Yes' : value === false ? 'No' : '—',
      getSortValue: (item: any) =>
        item.is3pl === true ? 'Yes' : item.is3pl === false ? 'No' : '',
    },
    { columnKey: 'category', header: 'Category', width: 140, renderCell: renderCategoryCell },
    {
      columnKey: 'status',
      header: 'Status',
      width: 120,
      formatter: (value: boolean | undefined | null) =>
        value === true ? 'Enabled' : value === false ? 'Disabled' : '',
      getSortValue: (item: any) =>
        item.status === true ? 'Enabled' : item.status === false ? 'Disabled' : '',
    },
    {
      columnKey: 'rackingSection',
      header: 'Racking Inspection Section',
      width: 230,
      formatter: formatRackingSection,
      getSortValue: (item: any) => formatRackingSection(item.rackingSection),
    },
    ...COMMON_END_COLS,
  ],
  'site-evaluation': [
    { columnKey: 'keyFocusArea', header: 'Key Focus Area', width: 280 },
    LOCATION_TYPE_COL,
    { columnKey: 'organizationType', header: 'Org Types', width: 170, renderCell: renderOrgTypeCell },
    {
      columnKey: 'is3pl',
      header: '3PL Status',
      width: 120,
      formatter: (value: boolean | undefined) =>
        value === true ? 'Yes' : value === false ? 'No' : '-',
      getSortValue: (item: any) =>
        item.is3pl === true ? 'Yes' : item.is3pl === false ? 'No' : '',
    },
    { columnKey: 'category', header: 'Category', width: 140, renderCell: renderCategoryCell },
    {
      columnKey: 'status',
      header: 'Status',
      width: 120,
      formatter: (value: boolean | undefined | null) =>
        value === true ? 'Enabled' : value === false ? 'Disabled' : '-',
      getSortValue: (item: any) =>
        item.status === true ? 'Enabled' : item.status === false ? 'Disabled' : '',
    },
    { columnKey: 'siteSection', header: 'Site Evaluation - Section', width: 230, formatter: (value: any) => (value != null && value !== '' ? value : '-') },
  ],
};

const KfaTable: React.FC<KfaTableProps> = ({ activeTab, currentPage, totalPages, pageSize, onPageChange, onPageSizeChange, filterOptions, onFilterChange, onResetFilters, selectedModule, searchText, onSearchChange, onSaveSuccess }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const items = useSelector((state: RootState) => state.configuration.keyFocusAreas.items);
  const totalElements = useSelector((state: RootState) => state.configuration.keyFocusAreas.stats.totalElements);
  const isLoading = useSelector((state: RootState) => state.configuration.keyFocusAreas.isLoading);
  const zoneMap = useSelector((state: RootState) => state.configuration.keyFocusAreas.zoneMap);
  const obsItems = useSelector((state: RootState) => state.configuration.observationsQuestions.items);
  const obsIsLoading = useSelector((state: RootState) => state.configuration.observationsQuestions.isLoading);
  const obsTotalCount = useSelector((state: RootState) => state.configuration.observationsQuestions.totalCount);
  const obsTotalPages = useSelector((state: RootState) => state.configuration.observationsQuestions.totalPages);

  const rackingItems = useSelector((state: RootState) => state.configuration.rackingInspection.items);
  const rackingIsLoading = useSelector((state: RootState) => state.configuration.rackingInspection.isLoading);
  const rackingTotalCount = useSelector((state: RootState) => state.configuration.rackingInspection.totalCount);
  const rackingTotalPages = useSelector((state: RootState) => state.configuration.rackingInspection.totalPages);

  const nhoItems = useSelector((state: RootState) => state.configuration.nhoQuestions.items);
  const nhoHeaders = useSelector((state: RootState) => state.configuration.nhoQuestions.headers);
  const nhoIsLoading = useSelector((state: RootState) => state.configuration.nhoQuestions.isLoading);
  const nhoTotalCount = useSelector((state: RootState) => state.configuration.nhoQuestions.totalCount);
  const nhoTotalPages = useSelector((state: RootState) => state.configuration.nhoQuestions.totalPages);

  const siteEvalItems = useSelector((state: RootState) => state.configuration.siteEvaluation.items);
  const siteEvalIsLoading = useSelector((state: RootState) => state.configuration.siteEvaluation.isLoading);
  const siteEvalTotalCount = useSelector((state: RootState) => state.configuration.siteEvaluation.totalCount);
  const siteEvalTotalPages = useSelector((state: RootState) => state.configuration.siteEvaluation.totalPages);

  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlayMode, setOverlayMode] = useState<'create' | 'edit'>('create');
  const [selectedKfa, setSelectedKfa] = useState<KfaEditItem | null>(null);

  // Observation tab overlay state
  const [obsOverlayVisible, setObsOverlayVisible] = useState(false);
  const [obsOverlayMode, setObsOverlayMode] = useState<'create' | 'edit'>('create');
  const [selectedObsItem, setSelectedObsItem] = useState<ObservationEditItem | null>(null);

  // Racking Inspection tab overlay state
  const [rackingOverlayVisible, setRackingOverlayVisible] = useState(false);
  const [rackingOverlayMode, setRackingOverlayMode] = useState<'create' | 'edit'>('create');
  const [selectedRackingItem, setSelectedRackingItem] = useState<RackingInspectionEditItem | null>(null);

  // NHO tab overlay state
  const [nhoOverlayVisible, setNhoOverlayVisible] = useState(false);
  const [nhoOverlayMode, setNhoOverlayMode] = useState<'create' | 'edit'>('create');
  const [selectedNhoItem, setSelectedNhoItem] = useState<NhoEditItem | null>(null);

  // Site Evaluation tab overlay state
  const [siteEvalOverlayVisible, setSiteEvalOverlayVisible] = useState(false);
  const [siteEvalOverlayMode, setSiteEvalOverlayMode] = useState<'create' | 'edit'>('create');
  const [selectedSiteEvalItem, setSelectedSiteEvalItem] = useState<SiteEvaluationEditItem | null>(null);

  const startRecord = totalElements === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, totalElements);

  // Helper: is this one of the KFA tabs that use the shared kfa questions API?
  const isKfaTab = activeTab === 'shift-handover' || activeTab === 'leadership-walk' || activeTab === 'sparc-walk';

  // Data for each tab
  const rawData =
    isKfaTab
      ? items.map((item) => mapMainTabItem(item, zoneMap))
      : activeTab === 'observations'
      ? obsItems.map(mapObservationsItem)
      : activeTab === 'racking-inspection'
      ? rackingItems.map(mapRackingInspectionItem)
      : activeTab === 'nho'
      ? nhoItems.map((item) => mapNhoItem(item, nhoHeaders))
      : activeTab === 'site-evaluation'
      ? siteEvalItems.map(mapSiteEvaluationItem)
      : [];

  const activeIsLoading = activeTab === 'observations' ? obsIsLoading
    : activeTab === 'racking-inspection' ? rackingIsLoading
    : activeTab === 'nho' ? nhoIsLoading
    : activeTab === 'site-evaluation' ? siteEvalIsLoading
    : isKfaTab ? isLoading
    : isLoading;
  const activeTotalPages = activeTab === 'observations' ? obsTotalPages
    : activeTab === 'racking-inspection' ? rackingTotalPages
    : activeTab === 'nho' ? nhoTotalPages
    : activeTab === 'site-evaluation' ? siteEvalTotalPages
    : totalPages;

  // NHO and Site Evaluation use server-side pagination; no client-side search re-filter
  const data = (activeTab === 'nho' || activeTab === 'site-evaluation')
    ? rawData
    : searchText.trim()
    ? rawData.filter((item) => {
        const q = searchText.toLowerCase();
        return (
          ((item as any).keyFocusArea ?? '').toLowerCase().includes(q) ||
          ((item as any).question ?? '').toLowerCase().includes(q)
        );
      })
    : rawData;

  const baseColumns = TAB_COLUMNS[activeTab];

  const { width: screenWidth } = useWindowDimensions();
  const isIOSMobile = Platform.OS === 'ios' && screenWidth < 768;
  const [tableContainerWidth, setTableContainerWidth] = useState(0);

  const computedColumns = useMemo(() => {
    const availableWidth = tableContainerWidth > 0 ? tableContainerWidth : screenWidth;
    if (availableWidth <= 0 || baseColumns.length === 0) return baseColumns;
    const colWidth = Math.floor(availableWidth / baseColumns.length);
    return baseColumns.map((col) => {
      let renderCell = col.renderCell;
      if (col.columnKey === 'category') {
        renderCell = (item: any) => renderCategoryCell(item, isDark);
      } else if (col.columnKey === 'organizationType') {
        renderCell = (item: any) => renderOrgTypeCell(item, isDark);
      } else if (col.columnKey === 'locationType') {
        renderCell = (item: any) => renderLocationTypeCell(item, isDark);
      }
      return { ...col, width: colWidth, renderCell };
    });
  }, [baseColumns, tableContainerWidth, screenWidth, isDark]);

  const description =
    isKfaTab
      ? isLoading
        ? 'Loading...'
        : totalElements === 0
        ? 'No records found'
        : `Showing ${startRecord}–${endRecord} of ${totalElements} record(s)`
      : activeTab === 'observations'
      ? obsIsLoading
        ? 'Loading...'
        : obsTotalCount === 0
        ? 'No records found'
        : `Showing ${(currentPage - 1) * pageSize + 1}\u2013${Math.min(currentPage * pageSize, obsTotalCount)} of ${obsTotalCount} record(s)`
      : activeTab === 'racking-inspection'
      ? rackingIsLoading
        ? 'Loading...'
        : rackingTotalCount === 0
        ? 'No records found'
        : `Showing ${(currentPage - 1) * pageSize + 1}\u2013${Math.min(currentPage * pageSize, rackingTotalCount)} of ${rackingTotalCount} record(s)`
      : activeTab === 'nho'
      ? nhoIsLoading
        ? 'Loading...'
        : nhoTotalCount === 0
        ? 'No records found'
        : `Showing ${(currentPage - 1) * pageSize + 1}\u2013${Math.min(currentPage * pageSize, nhoTotalCount)} of ${nhoTotalCount} record(s)`
      : activeTab === 'site-evaluation'
      ? siteEvalIsLoading
        ? 'Loading...'
        : siteEvalTotalCount === 0
        ? 'No records found'
        : `Showing ${(currentPage - 1) * pageSize + 1}\u2013${Math.min(currentPage * pageSize, siteEvalTotalCount)} of ${siteEvalTotalCount} record(s)`
      : 'No records found — API not yet integrated for this module';

  return (
    <View>
      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <View
          style={[
            styles.searchInputWrapper,
            isDark && {
              backgroundColor: colors.background.surface,
              borderColor: colors.border.default,
            },
          ]}
        >
          <MaterialIcons
            name="search"
            size={20}
            color={isDark ? colors.text.secondary : '#616161'}
            style={styles.searchIcon}
          />
          <TextInput
            style={[
              styles.searchInput,
              isDark && { color: colors.text.primary },
            ]}
            placeholder={
              activeTab === 'racking-inspection'
                ? 'Search by Key Focus Area'
                : activeTab === 'nho'
                ? 'Search by Key Focus Area '
                : activeTab === 'site-evaluation'
                ? 'Search by Key Focus Area'
                : 'Search by Key Focus Area or Question...'
            }
            placeholderTextColor={isDark ? colors.text.secondary : '#999999'}
            value={searchText}
            onChangeText={onSearchChange}
            returnKeyType="search"
          />
          {searchText.length > 0 && (
            <TouchableOpacity onPress={() => onSearchChange('')} style={styles.searchClear}>
              <MaterialIcons name="close" size={18} color={isDark ? colors.text.secondary : '#616161'} />
            </TouchableOpacity>
          )}
        </View>
      </View>

      <View style={styles.filterContainer}>
        <LHFilter
          filters={filterOptions}
          onFilterChange={onFilterChange}
          itemsPerRow={isIOSMobile ? 1 : 6}
          onClearAllFilters={onResetFilters}
        />
      </View>
      <View onLayout={(e) => setTableContainerWidth(e.nativeEvent.layout.width)}>
      <LHDataTable
        data={data}
        columns={computedColumns}
        tableHeader=""
        tableDescription={description}
        maxRecordsToShow={pageSize}
        isLoading={activeIsLoading}
        controlledPage={currentPage}
        controlledTotalPages={activeTotalPages}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        emptyMessage={
          searchText || filterOptions.some(f => f.selectedOptionId)
            ? 'No Key Focus Areas match your current filters and search criteria.'
            : 'No Key Focus Areas to display.'
        }
        onClearFilters={
          searchText || filterOptions.some(f => f.selectedOptionId)
            ? () => { onResetFilters(); }
            : undefined
        }
        onRowClick={(row) => {
          if (activeTab === 'observations') {
            // Find the original API item to get the raw multi-select arrays
            const apiItem = obsItems.find((item) => String(item.id) === row.id);
            
            // Log when opening ObservationAddOverlay for diagnostics
            console.log('KfaTable: observation row clicked', row, apiItem);
            
            // Open ObservationAddOverlay for observations tab rows
            setSelectedObsItem({
              id: row.id,
              observation: row.keyFocusArea,
              question: row.question ?? '',
              sparcCategory: row.category,
              locationType: row.locationType,
              organizationType: row.organizationType,
              is3pl: row.is3pl,
              threePlStatus: row.threePlStatus ?? (row.is3pl === true ? 'YES' : row.is3pl === false ? 'NO' : undefined),
              status: row.status,
              // Use normalized values from API item first (array/string-safe); fallback to row values
              observationTypeNames: (() => {
                const values = [
                  ...normalizeMultiValue((apiItem as any)?.observationTypeNames),
                  ...normalizeMultiValue((apiItem as any)?.observationTypeName),
                  ...normalizeMultiValue((apiItem as any)?.observationType),
                ];
                if (values.length > 0) return Array.from(new Set(values));
                return normalizeMultiValue(row.obsType);
              })(),
              roleNames: (() => {
                const values = [
                  ...normalizeMultiValue((apiItem as any)?.roleNames),
                  ...normalizeMultiValue((apiItem as any)?.roleName),
                  ...normalizeMultiValue((apiItem as any)?.roles),
                  ...normalizeMultiValue((apiItem as any)?.role),
                ];
                if (values.length > 0) return Array.from(new Set(values));
                return normalizeMultiValue(row.obsRoles);
              })(),
              section: row.obsSection,
              createdAt: row.createdAt,
            });
            setObsOverlayMode('edit');
            setObsOverlayVisible(true);
          } else if (activeTab === 'racking-inspection') {
            // Open RackingInspectionAddOverlay for racking-inspection tab rows
            const rawRackingItem = rackingItems.find((item) => String(item.rowKey) === String(row.id));
            setSelectedRackingItem({
              id: row.id,
              qtext: row.keyFocusArea ?? '',
              shortForm: row.question ?? '',
              section: row.rackingSection ?? '',
              // locationType is stored as a joined string in the table row — split back to array
              locationTypes: row.locationType
                ? row.locationType.split(', ').filter(Boolean)
                : [],
              organizationType: row.organizationType ?? '',
              threePlStatus: row.is3pl === true ? 'YES' : row.is3pl === false ? 'NO' : undefined,
              category: row.category ?? '',
              status: row.status,
              createdAt: row.createdAt,
              qnum: rawRackingItem?.qnum,
            });
            setRackingOverlayMode('edit');
            setRackingOverlayVisible(true);
          } else if (activeTab === 'nho') {
            // Look up the original NhoApiItem from Redux — do NOT use computed table column values.
            const apiItem = nhoItems.find((item) => String(item.id) === row.id);

            // Helper: find an item by id in the nhoHeaders array (contains Header + Sub-Header)
            const findHeader = (id?: number) =>
              id != null ? nhoHeaders.find((h) => h.id === id) : undefined;

            // Strip colons from header text (matches how headerOptions deduplicates)
            const cleanSV = (sv?: string | null) =>
              sv ? sv.replace(/:/g, '').trim() : undefined;

            // Resolve sectionHeader / sectionSubHeader from Redux parentId chain:
            //   Header    → sectionHeader = the item's own sectionValue
            //   Sub-Header → sectionSubHeader = own sectionValue; sectionHeader = parent Header
            //   Data      → sectionHeader = parent Header
            //   Sub-Data  → sectionSubHeader = parent Sub-Header; sectionHeader = grandparent Header
            let resolvedSectionHeader: string | undefined;
            let resolvedSectionSubHeader: string | undefined;

            // Priority: use API-provided text fields first; parentId lookup is a fallback
            // for items whose sectionHeader / sectionSubHeader are missing from the response.
            if (apiItem?.sectionType === 'Header') {
              resolvedSectionHeader = cleanSV(apiItem.sectionValue);
            } else if (apiItem?.sectionType === 'Sub-Header') {
              resolvedSectionSubHeader = apiItem.sectionValue ?? undefined;
              resolvedSectionHeader =
                cleanSV(apiItem.sectionHeader)
                ?? cleanSV(findHeader(apiItem.parentId)?.sectionValue);
            } else if (apiItem?.sectionType === 'Data') {
              resolvedSectionHeader =
                cleanSV(apiItem.sectionHeader)
                ?? cleanSV(findHeader(apiItem.parentId)?.sectionValue);
            } else if (apiItem?.sectionType === 'Sub-Data') {
              const parentSubHeader = findHeader(apiItem.parentId);
              resolvedSectionSubHeader =
                apiItem.sectionSubHeader ?? parentSubHeader?.sectionValue ?? undefined;
              resolvedSectionHeader =
                cleanSV(apiItem.sectionHeader)
                ?? cleanSV(findHeader(parentSubHeader?.parentId)?.sectionValue);
            }

            setSelectedNhoItem({
              id: row.id,
              qtext: apiItem?.sectionValue ?? row.keyFocusArea ?? '',
              sectionType: apiItem?.sectionType ?? '',
              touchTypes: Array.isArray(apiItem?.touchTypes) ? apiItem!.touchTypes : [],
              preferredMethod: Number(apiItem?.preferredMethod ?? 0),
              sectionHeader: resolvedSectionHeader,
              sectionSubHeader: resolvedSectionSubHeader,
              locationType: apiItem?.locationType ?? '',
              organizationType: apiItem?.organizationType ?? '',
              threePlStatus: typeof apiItem?.threePlStatus === 'string'
                ? apiItem.threePlStatus
                : (row.is3pl === true ? 'YES' : row.is3pl === false ? 'NO' : undefined),
              category: apiItem?.category ?? '',
              status: apiItem?.status ?? row.status,
              section: apiItem?.section != null && !isNaN(Number(apiItem.section)) ? Number(apiItem.section) : undefined,
              sortOrder: apiItem?.sortOrder != null && !isNaN(Number(apiItem.sortOrder)) ? Number(apiItem.sortOrder) : undefined,
              createdAt: apiItem?.createdAt ?? undefined,
            });
            setNhoOverlayMode('edit');
            setNhoOverlayVisible(true);
          } else if (activeTab === 'site-evaluation') {
            const apiItem = siteEvalItems.find((item) => String(item.id) === row.id);

            setSelectedSiteEvalItem({
              id: row.id,
              sectionType: apiItem?.sectionType ?? '',
              sectionValue: apiItem?.sectionValue ?? row.keyFocusArea ?? '',
              sectionHeader: apiItem?.sectionHeader ?? row.siteSection ?? null,
              sectionSubHeader: apiItem?.sectionSubHeader ?? null,
              locationType: apiItem?.locationType ?? row.locationType ?? '',
              status: apiItem?.status ?? row.status ?? true,
              organizationType: apiItem?.organizationType ?? row.organizationType ?? '',
              threePlStatus:
                apiItem?.threePlStatus ??
                (row.is3pl === true ? true : row.is3pl === false ? false : null),
              category: apiItem?.category ?? row.category ?? '',
              createdAt: apiItem?.createdAt ?? row.createdAt ?? undefined,
            });
            setSiteEvalOverlayMode('edit');
            setSiteEvalOverlayVisible(true);
          } else if (isKfaTab) {
            // Open KfaAddOverlay for shift-handover / leadership-walk / sparc-walk rows
            setSelectedKfa({
              id: row.id,
              negativeBehavior: row.keyFocusArea,
              question: row.question,
              sparcCategory: row.category,
              locationType: row.locationType,
              organizationType: row.organizationType,
              is3pl: row.is3pl,
              frequency: row.leadershipWalkFrequency,
              modules: row.modules,
              zoneIds: row.zoneIds ?? [],
              sparcSection: row.sparcWalkSection,
              status: row.status,
              createdAt: row.createdAt,
            });
            setOverlayMode('edit');
            setOverlayVisible(true);
          }
        }}
      />
      </View>
      {/* KFA overlay — for main / NHO / Racking Inspection / Site Evaluation rows */}
      <KfaAddOverlay
        visible={overlayVisible}
        onDismiss={() => {
          setOverlayVisible(false);
          setSelectedKfa(null);
          setOverlayMode('create');
        }}
        mode={overlayMode}
        selectedKfa={selectedKfa ?? undefined}
        selectedModule={selectedModule}
        onSaveSuccess={onSaveSuccess}
      />
      {/* Observation overlay — exclusively for observations tab rows */}
      <ObservationAddOverlay
        visible={obsOverlayVisible}
        onDismiss={() => {
          setObsOverlayVisible(false);
          setSelectedObsItem(null);
          setObsOverlayMode('create');
        }}
        mode={obsOverlayMode}
        selectedItem={selectedObsItem ?? undefined}
        onSaveSuccess={onSaveSuccess}
      />
      {/* Racking Inspection overlay — exclusively for racking-inspection tab rows */}
      <RackingInspectionAddOverlay
        visible={rackingOverlayVisible}
        onDismiss={() => {
          setRackingOverlayVisible(false);
          setSelectedRackingItem(null);
          setRackingOverlayMode('create');
        }}
        mode={rackingOverlayMode}
        selectedItem={selectedRackingItem ?? undefined}
        onSaveSuccess={onSaveSuccess}
      />
      {/* NHO overlay — exclusively for nho tab rows */}
      <NhoAddOverlay
        visible={nhoOverlayVisible}
        onDismiss={() => {
          setNhoOverlayVisible(false);
          setSelectedNhoItem(null);
          setNhoOverlayMode('create');
        }}
        mode={nhoOverlayMode}
        selectedItem={selectedNhoItem ?? undefined}
        onSaveSuccess={onSaveSuccess}
      />
      <SiteEvaluationOverlay
        visible={siteEvalOverlayVisible}
        onDismiss={() => {
          setSiteEvalOverlayVisible(false);
          setSelectedSiteEvalItem(null);
          setSiteEvalOverlayMode('create');
        }}
        mode={siteEvalOverlayMode}
        selectedItem={selectedSiteEvalItem ?? undefined}
        onSaveSuccess={onSaveSuccess}
      />
    </View>
  );
};


export default KfaTable;
